import { useState, useEffect } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  Link,
  useLocation,
} from "react-router-dom";
import { checkAuth, logout } from "./lib/api";
import { LoginPage } from "./pages/Login";
import { DashboardPage } from "./pages/Dashboard";
import { WatcherEditPage } from "./pages/WatcherEdit";
import { SettingsPage } from "./pages/Settings";
import { HistoryPage } from "./pages/History";

function AuthGuard({ children }: { children: React.ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);

  useEffect(() => {
    checkAuth()
      .then((res) => setIsAuthenticated(res.authenticated))
      .catch(() => setIsAuthenticated(false));
  }, []);

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen flex flex-col">
        <div className="flex-1 flex justify-center items-center">
          <div className="w-8 h-8 border-2 border-gray-200 border-t-slate-600 rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function NavLink({ to, children, onClick }: { to: string; children: React.ReactNode; onClick?: () => void }) {
  const location = useLocation();
  const isActive =
    location.pathname === to ||
    (to !== "/" && location.pathname.startsWith(to));

  return (
    <Link
      to={to}
      onClick={onClick}
      className={`text-white no-underline px-4 py-2 rounded ${
        isActive ? "bg-white/20" : "hover:bg-white/10"
      }`}
    >
      {children}
    </Link>
  );
}

function Layout({ children }: { children: React.ReactNode }) {
  const [menuOpen, setMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    window.location.href = "/login";
  };

  const closeMenu = () => setMenuOpen(false);

  return (
    <div className="min-h-screen flex flex-col">
      <header className="bg-slate-700 text-white">
        {/* Desktop header */}
        <div className="max-w-6xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4 md:gap-8">
            <h1 className="text-lg md:text-2xl font-bold whitespace-nowrap">seats.aero Watcher</h1>
            {/* Desktop nav */}
            <nav className="hidden md:flex gap-2">
              <NavLink to="/">Dashboard</NavLink>
              <NavLink to="/history">History</NavLink>
              <NavLink to="/settings">Settings</NavLink>
            </nav>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleLogout}
              className="hidden md:inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-lg bg-white/10 text-white hover:bg-white/20 transition-colors cursor-pointer border-none"
            >
              Logout
            </button>
            {/* Mobile menu button */}
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="md:hidden inline-flex items-center justify-center p-2 rounded-lg bg-white/10 text-white hover:bg-white/20 transition-colors cursor-pointer border-none"
              aria-label="Toggle menu"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {menuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>
        {/* Mobile nav dropdown */}
        {menuOpen && (
          <nav className="md:hidden border-t border-white/10 px-4 py-2 flex flex-col gap-1">
            <NavLink to="/" onClick={closeMenu}>Dashboard</NavLink>
            <NavLink to="/history" onClick={closeMenu}>History</NavLink>
            <NavLink to="/settings" onClick={closeMenu}>Settings</NavLink>
            <button
              onClick={() => { closeMenu(); handleLogout(); }}
              className="text-left text-white px-4 py-2 rounded hover:bg-white/10 transition-colors cursor-pointer border-none bg-transparent"
            >
              Logout
            </button>
          </nav>
        )}
      </header>
      <main className="flex-1 py-4 md:py-8 px-4">
        <div className="max-w-6xl mx-auto">{children}</div>
      </main>
    </div>
  );
}

export function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route
          path="/"
          element={
            <AuthGuard>
              <Layout>
                <DashboardPage />
              </Layout>
            </AuthGuard>
          }
        />
        <Route
          path="/watcher/new"
          element={
            <AuthGuard>
              <Layout>
                <WatcherEditPage />
              </Layout>
            </AuthGuard>
          }
        />
        <Route
          path="/watcher/:id"
          element={
            <AuthGuard>
              <Layout>
                <WatcherEditPage />
              </Layout>
            </AuthGuard>
          }
        />
        <Route
          path="/history"
          element={
            <AuthGuard>
              <Layout>
                <HistoryPage />
              </Layout>
            </AuthGuard>
          }
        />
        <Route
          path="/settings"
          element={
            <AuthGuard>
              <Layout>
                <SettingsPage />
              </Layout>
            </AuthGuard>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
